import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, ScrollView, View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairySupportButton from "../../../Components/ToothFairyComponents/ToothFairySupportButton";
import ToothFairySwitch from "../../../Components/ToothFairyComponents/ToothFairySwitch";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import TopicsSelectionComponent from "../../MrcDocuments/Components/TopicsSelectionComponent";
import validateTrainingJsonl from "../Utils/validateTrainingJsonl";

const DOCS_URL =
  "https://docs.toothfairyai.com/docs/Training/training-data#import-of-training-data";

const HEALTH_CHECK_SAMPLE_BYTES = 20 * 1024 * 1024;

const SHAPE_SFT = `{
  "messages": [
    { "role": "system", "content": "System message" },
    { "role": "user", "content": "User message" },
    { "role": "assistant", "content": "Assistant message" }
  ]
}`;

const SHAPE_DPO = `{
  "input": {
    "messages": [
      { "role": "user", "content": "User message" },
      { "role": "assistant", "content": "Assistant message" }
    ]
  },
  "preferred_output": [
    { "role": "assistant", "content": "Assistant message" }
  ],
  "non_preferred_output": [
    { "role": "assistant", "content": "Assistant message (non preferred)" }
  ]
}`;

const SHAPE_RL = `{
  "task_spec": {
    "prompt": "Create a file named hello.txt containing exactly: Hello World",
    "env": "tfcode",
    "max_steps": 16,
    "system_message": "Optional premise for the task",
    "checker": {
      "type": "exit_code",
      "config": { "command": "grep -q 'Hello World' hello.txt", "expected_code": 0 }
    }
  }
}`;

const STEPS = ["type", "file", "health", "upload"];

const DocumentsImportWizard = ({
  isModalOpen,
  setIsModalOpen,
  documentTypes,
  selectedImportType,
  setSelectedImportType,
  filePathGeneric,
  handleFileSelect,
  handleImportsGeneric,
  removeFile,
  batchUploadStatus,
  isFileUploadInProgress,
  topicsForImport,
  setSelectedTopicsForImport,
  onTopicCreated = () => {},
  importScope,
  setImportScope,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { getWorkspaceEntities } = useWorkspaces();
  const topics = getWorkspaceEntities("topic");

  const [stepIndex, setStepIndex] = useState(0);
  const [validation, setValidation] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  const filesArray = useMemo(() => {
    if (!filePathGeneric) return [];
    return Array.from(filePathGeneric);
  }, [filePathGeneric]);

  const typeObj = useMemo(
    () =>
      (documentTypes || Constants.DOCUMENT_TYPES).find(
        (u) => u.key === selectedImportType
      ) || (documentTypes || Constants.DOCUMENT_TYPES)[0],
    [documentTypes, selectedImportType]
  );

  useEffect(() => {
    if (isModalOpen) {
      setStepIndex(0);
      setValidation(null);
      setIsChecking(false);
    }
  }, [isModalOpen]);

  const runHealthCheck = async () => {
    setIsChecking(true);
    setValidation(null);
    try {
      if (filesArray.length === 0) {
        setValidation({
          isValid: false,
          errors: [
            t("documents.wizard.noFile", { defaultValue: "No file selected" }),
          ],
          validCount: 0,
          totalCount: 0,
        });
        return;
      }
      const aggregate = {
        isValid: true,
        errors: [],
        validCount: 0,
        totalCount: 0,
        sampled: false,
      };
      for (const file of filesArray) {
        let text;
        let sampled = false;
        if (file.size > HEALTH_CHECK_SAMPLE_BYTES) {
          const slice = await file.slice(0, HEALTH_CHECK_SAMPLE_BYTES).text();
          const cut = slice.lastIndexOf("\n");
          text = cut > 0 ? slice.slice(0, cut) : slice;
          sampled = true;
        } else {
          text = await file.text();
        }
        const result = validateTrainingJsonl(text, typeObj?.key);
        aggregate.sampled = aggregate.sampled || sampled;
        aggregate.totalCount += result.totalCount || 0;
        aggregate.validCount += result.validCount || 0;
        if (!result.isValid) {
          aggregate.isValid = false;
          const fileErrors = result.errors?.length
            ? result.errors
            : [t("documents.wizard.invalid", { defaultValue: "Invalid" })];
          fileErrors.forEach((err) =>
            aggregate.errors.push(`${file.name}: ${err}`)
          );
        }
      }
      setValidation(aggregate);
    } catch (e) {
      setValidation({
        isValid: false,
        errors: [
          t("documents.wizard.readError", {
            defaultValue: "Could not read file",
          }),
        ],
        validCount: 0,
        totalCount: 0,
      });
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    if (STEPS[stepIndex] === "health") {
      runHealthCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, filesArray]);

  const canGoNext = () => {
    const step = STEPS[stepIndex];
    if (step === "type") return !!typeObj?.key;
    if (step === "file") return filesArray.length > 0;
    if (step === "health") return validation?.isValid;
    return true;
  };

  const next = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIndex((i) => Math.max(i - 1, 0));

  const closeModal = () => setIsModalOpen(false);

  const renderStepper = () => (
    <View style={{ flexDirection: "row", marginBottom: 16 }}>
      {STEPS.map((s, i) => {
        const active = i === stepIndex;
        const done = i < stepIndex;
        return (
          <View
            key={s}
            style={{
              flex: 1,
              paddingVertical: 6,
              marginRight: 4,
              borderRadius: 6,
              alignItems: "center",
              backgroundColor: active
                ? currentTheme?.theme?.primary
                : done
                ? currentTheme?.theme?.softBackgroundColor
                : currentTheme?.theme?.transparent,
            }}
          >
            <AppText
              style={{ fontSize: 12 }}
              color={
                active
                  ? currentTheme?.theme?.backgroundColor
                  : currentTheme?.theme?.darkGrey
              }
            >
              {i + 1}. {t(`documents.wizard.steps.${s}`, { defaultValue: s })}
            </AppText>
          </View>
        );
      })}
    </View>
  );

  const renderTypeStep = () => (
    <View>
      <AppText style={{ marginBottom: 8 }}>
        {t("documents.wizard.chooseType", {
          defaultValue: "Choose the dataset type",
        })}
      </AppText>
      <AppDropDown
        dropDownStyle={{
          width: "100%",
          borderColor: currentTheme?.theme?.border_color,
          marginBottom: 16,
        }}
        isElevated={false}
        dropdownIconColor="transparent"
        items={(documentTypes || Constants.DOCUMENT_TYPES).map((u) => ({
          id: u.key,
          label: `${t(u.labelKey, {
            defaultValue: u.label,
          })} — ${t(u.methodsKey, { defaultValue: "" })}`.replace(/ — $/, ""),
        }))}
        propertyLabel="label"
        propertyValue="id"
        selectedValue={typeObj?.key}
        onValueChange={(v) => setSelectedImportType(v)}
      />
      {typeObj?.descKey && (
        <AppText
          style={{ marginBottom: 12, fontSize: 12 }}
          color={currentTheme?.theme?.darkGrey}
        >
          {t(typeObj.descKey)}
        </AppText>
      )}
      <View
        style={{
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <AppText fontWeight="bold" style={{ marginBottom: 6 }}>
          {t("documents.wizard.expectedShape", {
            defaultValue: "Expected JSONL shape",
          })}
        </AppText>
        <AppText
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
          }}
        >
          {typeObj?.key === "generativeTraining"
            ? SHAPE_DPO
            : typeObj?.key === "rlTask"
              ? SHAPE_RL
              : SHAPE_SFT}
        </AppText>
      </View>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <AppText
          color={currentTheme?.theme?.primary}
          isClickable
          leftIcon={AppAssets?.icons?.info}
          iconSize={14}
          onPress={() => window.open(DOCS_URL, "_blank")}
          style={{ marginLeft: 6 }}
        >
          {t("documents.wizard.readDocs", {
            defaultValue: "Read the import docs",
          })}
        </AppText>
        <ToothFairySupportButton _url={DOCS_URL} />
      </View>
    </View>
  );

  const renderFileStep = () => (
    <View>
      <AppText style={{ marginBottom: 12 }}>
        {t("documents.wizard.selectJsonl", {
          defaultValue: "Select a .jsonl file",
        })}
      </AppText>
      <ToothFairyButton
        title={t("documents.wizard.browse", { defaultValue: "Browse files" })}
        onPress={handleFileSelect}
        wrapperStyle={{ alignSelf: "flex-start", marginBottom: 16 }}
      />
      <AppText
        fontWeight="bold"
        color={currentTheme?.theme?.primary}
        style={{ marginBottom: 8 }}
      >
        {t("documents.selectedFiles")}
      </AppText>
      {filesArray.length === 0 ? (
        <AppText>{t("documents.noFileSelected")}</AppText>
      ) : (
        filesArray.map((file) => (
          <View
            key={file?.name}
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: 6,
            }}
          >
            <AppText
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: 360,
              }}
              fontWeight="bold"
            >
              {file?.name}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {batchUploadStatus[file?.name] != null && (
                <AppText
                  color={
                    batchUploadStatus[file?.name] == 100
                      ? currentTheme?.theme?.green
                      : currentTheme?.theme?.orange
                  }
                >
                  {` ${Math.floor(batchUploadStatus[file?.name])}%`}
                </AppText>
              )}
              {removeFile && !isFileUploadInProgress && (
                <AppIcon
                  color={currentTheme?.theme?.red}
                  isClickable
                  icon={AppAssets.icons.delete}
                  size={14}
                  onPress={() => removeFile(file?.name)}
                  style={{ marginLeft: 12 }}
                />
              )}
            </View>
          </View>
        ))
      )}
    </View>
  );

  const renderHealthStep = () => (
    <View>
      <AppText style={{ marginBottom: 12 }}>
        {t("documents.wizard.healthDescription", {
          defaultValue: "Checking the structure of your JSONL before upload",
        })}
      </AppText>
      {isChecking ? (
        <AppText>
          {t("documents.wizard.checking", { defaultValue: "Checking..." })}
        </AppText>
      ) : validation ? (
        <View>
          <View
            style={{
              flexDirection: "row",
              marginBottom: 12,
              padding: 12,
              borderRadius: 10,
              backgroundColor: validation.isValid
                ? currentTheme?.theme?.softBackgroundColor
                : currentTheme?.theme?.transparent,
            }}
          >
            <AppText
              color={
                validation.isValid
                  ? currentTheme?.theme?.green
                  : currentTheme?.theme?.red
              }
              fontWeight="bold"
              style={{ marginRight: 16 }}
            >
              {validation.isValid
                ? t("documents.wizard.valid", { defaultValue: "Valid" })
                : t("documents.wizard.invalid", { defaultValue: "Invalid" })}
            </AppText>
            <AppText>
              {t("documents.wizard.lines", { defaultValue: "Lines" })}:{" "}
              {validation.totalCount} —{" "}
              {t("documents.wizard.validLines", { defaultValue: "valid" })}:{" "}
              {validation.validCount}
            </AppText>
          </View>
          {validation.sampled && (
            <AppText
              color={currentTheme?.theme?.orange}
              style={{ fontSize: 12, marginBottom: 8 }}
            >
              {t("documents.wizard.sampledNote", {
                defaultValue:
                  "Large file — structure was checked on the first {{count}} lines only",
                count: validation.totalCount,
              })}
            </AppText>
          )}
          {validation.errors?.length > 0 && (
            <View
              style={{
                maxHeight: 220,
                borderWidth: 1,
                borderColor: currentTheme?.theme?.lightGrey,
                borderRadius: 8,
                padding: 10,
              }}
            >
              <ScrollView>
                {validation.errors.map((err, i) => (
                  <AppText
                    key={i}
                    color={currentTheme?.theme?.red}
                    style={{ fontSize: 12, marginBottom: 4 }}
                  >
                    {err}
                  </AppText>
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      ) : (
        <AppText>
          {t("documents.wizard.checking", { defaultValue: "Checking..." })}
        </AppText>
      )}
    </View>
  );

  const renderUploadStep = () => (
    <View>
      <AppText style={{ marginBottom: 12 }}>
        {t("documents.wizard.topicsAndUpload", {
          defaultValue: "Pick topics and upload",
        })}
      </AppText>
      <TopicsSelectionComponent
        showAddNew
        onTopicCreated={onTopicCreated}
        showEditIcon={false}
        isTopicUpdateModeEnabled
        selectedTopics={topicsForImport}
        onSelectedTopicsChange={(v) => setSelectedTopicsForImport(v)}
        topics={topics}
        wrapperContainerStyle={{ flex: 1 }}
        wrapperStyle={{ marginBottom: 20 }}
      />
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <ToothFairySwitch
          wrapperStyle={{
            flex: 1,
            marginRight: 7,
            // backgroundColor: currentTheme?.theme?.inputBackgroundColor,
          }}
          label={t("documents.wizard.validationScope", {
            defaultValue: "Import as validation data",
          })}
          onChange={(v) => {
            setImportScope(v ? "validation" : "training");
          }}
          isTicked={importScope === "validation"}
        />
      </View>
    </View>
  );

  const renderStep = () => {
    const step = STEPS[stepIndex];
    if (step === "type") return renderTypeStep();
    if (step === "file") return renderFileStep();
    if (step === "health") return renderHealthStep();
    return renderUploadStep();
  };

  const isLastStep = stepIndex === STEPS.length - 1;

  return (
    <ToothFairyModal
      transparent
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      isCloseButtonVisible={!isFileUploadInProgress}
      modalTitle={t("documents.modals.importTrainingData")}
      animationType="none"
      modalBackdrop={currentTheme?.theme?.softBackgroundColor}
      modalStyle={
        Platform.OS === "web"
          ? {
              maxWidth: 640,
              height: "fit-content",
              maxHeight: "90%",
              margin: "auto",
              marginHorizontal: "auto",
              borderRadius: 20,
            }
          : {}
      }
      modalInnerContentStyle={{ paddingHorizontal: 30, paddingVertical: 16 }}
    >
      {renderStepper()}
      {renderStep()}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 24,
        }}
      >
        <ToothFairyButton
          title={t("common.back", { defaultValue: "Back" })}
          onPress={back}
          isDisabled={stepIndex === 0 || isFileUploadInProgress}
          wrapperStyle={{ minWidth: 100 }}
        />
        {isLastStep ? (
          <ToothFairyButton
            title={t("common.upload")}
            onPress={handleImportsGeneric}
            isDisabled={isFileUploadInProgress || !validation?.isValid}
            wrapperStyle={{ minWidth: 120 }}
          />
        ) : (
          <ToothFairyButton
            title={t("common.next", { defaultValue: "Next" })}
            onPress={next}
            isDisabled={!canGoNext()}
            wrapperStyle={{ minWidth: 120 }}
          />
        )}
      </View>
    </ToothFairyModal>
  );
};

export default DocumentsImportWizard;
